import React from "react";
import ReactDOM from "react-dom/client";
import "./index.css";
import AppRouter from "./AppRouter";

/**
 * PERSISTENT LOGIN - UBER/LYFT STYLE
 * 
 * Once a user completes onboarding, they should NEVER see login screens again.
 * Opening the app (from homescreen or browser) goes straight to their dashboard.
 * 
 * Storage Strategy:
 * 1. localStorage (primary) - Fast, synchronous
 * 2. IndexedDB (backup) - More persistent on iOS Safari
 * 
 * The session persists indefinitely until the user explicitly signs out.
 */

const CLIENT_DATA_KEY = 'ova_client_data';
const DB_NAME = 'ova_dispatch_db';
const STORE_NAME = 'client_store';

// IndexedDB helper for iOS Safari persistence
const openDB = () => {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, 1);
    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);
    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'key' });
      }
    };
  });
};

const saveToIndexedDB = async (data) => {
  try {
    const db = await openDB();
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    store.put({ key: CLIENT_DATA_KEY, value: data });
    await tx.complete;
  } catch (e) {
    console.log('IndexedDB save failed:', e);
  }
};

const getFromIndexedDB = async () => {
  try {
    const db = await openDB();
    const tx = db.transaction(STORE_NAME, 'readonly');
    const store = tx.objectStore(STORE_NAME);
    const request = store.get(CLIENT_DATA_KEY);
    return new Promise((resolve) => {
      request.onsuccess = () => resolve(request.result?.value || null);
      request.onerror = () => resolve(null);
    });
  } catch (e) {
    console.log('IndexedDB read failed:', e);
    return null;
  }
};

// Sync localStorage and IndexedDB
const syncStorage = async () => {
  const localData = localStorage.getItem(CLIENT_DATA_KEY);
  const indexedData = await getFromIndexedDB();
  
  if (localData && !indexedData) {
    // localStorage has data, sync to IndexedDB
    await saveToIndexedDB(JSON.parse(localData));
  } else if (!localData && indexedData) {
    // IndexedDB has data (iOS Safari may have cleared localStorage), restore it
    localStorage.setItem(CLIENT_DATA_KEY, JSON.stringify(indexedData));
    return indexedData;
  }
  
  return localData ? JSON.parse(localData) : null;
};

// PERSISTENT SESSION CHECK - Runs BEFORE React renders
(async function checkPWASession() {
  const isStandalone = window.matchMedia('(display-mode: standalone)').matches 
    || window.navigator.standalone === true
    || document.referrer.includes('android-app://');
  
  const currentPath = window.location.pathname;
  const isRootOrLanding = currentPath === '/' || currentPath === '/landing' || currentPath === '/onboard';
  
  // Sync storage and get client data
  let clientData = null;
  try {
    clientData = await syncStorage();
  } catch (e) {
    const localData = localStorage.getItem(CLIENT_DATA_KEY);
    if (localData) {
      clientData = JSON.parse(localData);
    }
  }
  
  // If user has saved profile with valid identifier
  if (clientData && (clientData.client_id || clientData.phone || clientData.email)) {
    console.log('PWA Session - Found saved client:', clientData.client_id || clientData.phone || clientData.email);
    
    // In standalone mode (PWA from homescreen), ALWAYS go to client dashboard
    // This ensures the homescreen icon ALWAYS opens their account
    if (isStandalone) {
      if (!currentPath.startsWith('/client') && !currentPath.startsWith('/ride')) {
        console.log('PWA Standalone - Redirecting to client dashboard');
        window.location.replace('/client');
        return;
      }
    }
    
    // In browser mode, redirect from root/landing to client dashboard
    if (isRootOrLanding) {
      console.log('PWA Browser - Redirecting from landing to client dashboard');
      window.location.replace('/client');
      return;
    }
  }
})();

// Export storage helpers for use in components
window.ovaStorage = {
  saveClientData: async (data) => {
    localStorage.setItem(CLIENT_DATA_KEY, JSON.stringify(data));
    await saveToIndexedDB(data);
  },
  getClientData: async () => {
    return await syncStorage();
  },
  clearClientData: async () => {
    localStorage.removeItem(CLIENT_DATA_KEY);
    try {
      const db = await openDB();
      const tx = db.transaction(STORE_NAME, 'readwrite');
      const store = tx.objectStore(STORE_NAME);
      store.delete(CLIENT_DATA_KEY);
    } catch (e) {
      console.log('IndexedDB clear failed:', e);
    }
  }
};

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <AppRouter />
);

// Prevent PWA install prompts (we handle this manually after onboarding)
window.addEventListener('beforeinstallprompt', (e) => {
  e.preventDefault();
  console.log('PWA install prompt prevented');
});

// ---------------------------------------------------------------------------
// Self-healing chunk-load recovery.
//
// After a redeploy, an old cached main.js can reference chunk hashes that no
// longer exist on the server. That surfaces as `ChunkLoadError` (or a plain
// script network failure) and causes a white screen. When we detect it, we
// nuke every cache, unregister the SW, and hard-reload ONCE (guarded by a
// sessionStorage flag so we never loop).
// ---------------------------------------------------------------------------
const CHUNK_RECOVERY_FLAG = 'ova_chunk_recovery_attempted';

const recoverFromStaleBundle = async () => {
  if (sessionStorage.getItem(CHUNK_RECOVERY_FLAG)) return; // already tried
  sessionStorage.setItem(CHUNK_RECOVERY_FLAG, '1');
  try {
    if ('caches' in window) {
      const keys = await window.caches.keys();
      await Promise.all(keys.map((k) => window.caches.delete(k)));
    }
    if ('serviceWorker' in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map((r) => r.unregister()));
    }
  } catch (_) { /* best effort */ }
  window.location.reload();
};

const isChunkLoadError = (err) => {
  if (!err) return false;
  const name = err.name || '';
  const msg = err.message || String(err);
  return (
    name === 'ChunkLoadError' ||
    /Loading chunk [\w-]+ failed/i.test(msg) ||
    /Loading CSS chunk [\w-]+ failed/i.test(msg) ||
    /Failed to fetch dynamically imported module/i.test(msg)
  );
};

window.addEventListener('error', (event) => {
  if (isChunkLoadError(event.error) || isChunkLoadError(event)) {
    recoverFromStaleBundle();
  }
});

window.addEventListener('unhandledrejection', (event) => {
  if (isChunkLoadError(event.reason)) {
    recoverFromStaleBundle();
  }
});

// Clear the recovery flag once React has actually mounted successfully, so
// the next real chunk failure in a later session can trigger recovery again.
window.addEventListener('load', () => {
  setTimeout(() => sessionStorage.removeItem(CHUNK_RECOVERY_FLAG), 5000);
});

// Register Service Worker (auto-accept new versions to prevent stale bundles
// from lingering after a deploy).
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js')
      .then((registration) => {
        console.log('SW registered: ', registration);
        // If a waiting worker is already installed, activate it immediately.
        if (registration.waiting) {
          registration.waiting.postMessage({ type: 'SKIP_WAITING' });
        }
        registration.addEventListener('updatefound', () => {
          const newWorker = registration.installing;
          if (!newWorker) return;
          newWorker.addEventListener('statechange', () => {
            if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
              newWorker.postMessage({ type: 'SKIP_WAITING' });
            }
          });
        });
      })
      .catch((registrationError) => {
        console.log('SW registration failed: ', registrationError);
      });

    // When the active SW changes (a new deploy), do a single silent reload
    // to make sure the page runs against the fresh assets. Guarded to fire
    // at most once per page load.
    let hasReloadedForSW = false;
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      if (hasReloadedForSW) return;
      hasReloadedForSW = true;
      window.location.reload();
    });
  });
}
